import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { getFirebaseBackend } from 'src/app/authUtils';
import { MyProductionService } from 'src/app/services/my-production/my-production.service';
import { LoaderService } from 'src/app/services/loader/loader.service';
import Swal from 'sweetalert2';
import { RingcentralService } from 'src/app/services/ringcentral/ringcentral.service';

@Component({
	selector: 'app-my_production',
	templateUrl: './my_production.component.html',
	styleUrls: ['./my_production.component.scss'],
})
export class My_productionComponent implements OnInit {
	weekLabel: string = '';
	weekStart: Date = this.getWeekStart(new Date());
	leads: any[] = [];
	userId!: string;
	isLoading = false;
	appointmentsThisWeek: number;
	onlineUsers: any[] = [];

	dialsToday: number = 0;
	dashboardStats: any = null;
	totalDials = this.onlineUsers.reduce((sum, u) => sum + (u.dials || 0), 0);

	showWeekPicker = false;
	calendarMonth: Date = new Date(this.weekStart.getFullYear(), this.weekStart.getMonth(), 1);
	weekdayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

	@ViewChild('weekPickerToggle') weekPickerToggleRef!: ElementRef<HTMLButtonElement>;
	weekPickerPosition = { top: 0, left: 0 };

	constructor(
		private prodSrv: MyProductionService,
		private loaderSrv: LoaderService,
		private ringCentralSrv: RingcentralService,
		private dashboardSrv: RingcentralService,
	) {}

	ngOnInit() {
		this.loaderSrv.loading$.subscribe((loading) => {
			this.isLoading = loading;
		});

		const authUser = getFirebaseBackend()?.getAuthenticatedUser();
		this.userId = authUser?.IdUser;

		if (!this.userId) {
			Swal.fire('Error', 'No se encontró el usuario autenticado', 'error');
			return;
		}

		this.weekLabel = this.getWeekLabel(this.weekStart);
		this.loadLeads();
	}

	goToPreviousWeek(): void {
		const prev = new Date(this.weekStart);
		prev.setDate(prev.getDate() - 7);
		this.setWeek(prev);
	}

	goToNextWeek(): void {
		if (this.isCurrentWeek) return;
		const next = new Date(this.weekStart);
		next.setDate(next.getDate() + 7);
		this.setWeek(next);
	}

	private setWeek(date: Date): void {
		this.weekStart = this.getWeekStart(date);
		this.weekLabel = this.getWeekLabel(this.weekStart);
		this.loadLeads();
	}

	get isCurrentWeek(): boolean {
		const currentWeekStart = this.getWeekStart(new Date());
		return this.weekStart.getTime() >= currentWeekStart.getTime();
	}

	get weekStartISO(): string {
		return this.toISODate(this.weekStart);
	}

	private toISODate(date: Date): string {
		const y = date.getFullYear();
		const m = String(date.getMonth() + 1).padStart(2, '0');
		const d = String(date.getDate()).padStart(2, '0');
		return `${y}-${m}-${d}`;
	}

	private getWeekLabel(weekStart: Date): string {
		const formatted = this.formatWeekStart(weekStart);
		return `Week of ${formatted}`;
	}

	private getWeekStart(date: Date): Date {
		const d = new Date(date);
		const day = d.getDay();
		const diff = day === 0 ? -6 : 1 - day;
		d.setDate(d.getDate() + diff);
		d.setHours(0, 0, 0, 0);
		return d;
	}

	private formatWeekStart(date: Date): string {
		return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
	}

	closeWeekPicker(): void {
		this.showWeekPicker = false;
	}

	shiftCalendarMonth(delta: number): void {
		this.calendarMonth = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() + delta, 1);
	}

	get calendarMonthLabel(): string {
		return this.calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
	}

	get calendarWeeks(): { date: Date; otherMonth: boolean }[][] {
		const year = this.calendarMonth.getFullYear();
		const month = this.calendarMonth.getMonth();
		const firstOfMonth = new Date(year, month, 1);
		const lastOfMonth = new Date(year, month + 1, 0);

		const firstWeekStart = this.getWeekStart(firstOfMonth);
		const lastWeekStart = this.getWeekStart(lastOfMonth);

		const weeks: { date: Date; otherMonth: boolean }[][] = [];
		const cursor = new Date(firstWeekStart);

		while (cursor.getTime() <= lastWeekStart.getTime()) {
			const row: { date: Date; otherMonth: boolean }[] = [];
			for (let d = 0; d < 7; d++) {
				row.push({ date: new Date(cursor), otherMonth: cursor.getMonth() !== month });
				cursor.setDate(cursor.getDate() + 1);
			}
			weeks.push(row);
		}

		return weeks;
	}

	selectWeekFromCalendar(date: Date): void {
		if (this.isFutureWeek(date)) return;
		this.setWeek(date);
		this.showWeekPicker = false;
	}

	isSelectedWeek(date: Date): boolean {
		return this.getWeekStart(date).getTime() === this.weekStart.getTime();
	}

	isFutureWeek(date: Date): boolean {
		const currentWeekStart = this.getWeekStart(new Date());
		return this.getWeekStart(date).getTime() > currentWeekStart.getTime();
	}

	loadLeads(): void {
		this.loaderSrv.show();
		this.prodSrv.getThisWeeksLeads(this.userId, this.weekStartISO).subscribe(
			(res) => {
				this.loaderSrv.hide();
				if (res.success) {
					this.leads = res.body;
					this.appointmentsThisWeek = res.body[0]?.appointments_this_week || 0;
				} else {
					this.leads = [];
					this.appointmentsThisWeek = 0;
					Swal.fire('Notice', 'No leads found for this week', 'info');
				}
			},
			(err) => {
				this.loaderSrv.hide();
				this.appointmentsThisWeek = 0;
				Swal.fire('Error', 'An error occurred while loading leads', 'error');
			},
		);
	}

	toggleWeekPicker(): void {
		this.showWeekPicker = !this.showWeekPicker;
		if (this.showWeekPicker) {
			this.calendarMonth = new Date(this.weekStart.getFullYear(), this.weekStart.getMonth(), 1);
			this.updateWeekPickerPosition();
		}
	}

	private updateWeekPickerPosition(): void {
		const rect = this.weekPickerToggleRef.nativeElement.getBoundingClientRect();
		const dropdownWidth = 260;
		this.weekPickerPosition = {
			top: rect.bottom + 4,
			left: Math.max(8, rect.right - dropdownWidth),
		};
	}

	@HostListener('window:scroll')
	@HostListener('window:resize')
	onWindowChange(): void {
		if (this.showWeekPicker) {
			this.closeWeekPicker();
		}
	}
}
